import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Sparkles, 
  MessageSquare, 
  TrendingUp, 
  Users, 
  AlertTriangle,
  Send,
  Loader2
} from 'lucide-react';
import { useVoiceTaskDrafts } from '@/hooks/useVoiceTaskDrafts';
import { VoiceCaptureButton } from '@/components/ai/VoiceCaptureButton';
import { TaskDraftList } from '@/components/ai/TaskDraftList';
import { ListenButton } from '@/components/ListenButton';
import { ModelPicker } from '@/components/ai/ModelPicker';
import { useAiModels } from '@/hooks/useAiModels';
import '@/styles/ai.css';
import '@/styles/chat.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIAssistant({ user }) {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activeTab, setActiveTab] = useState('query');
  const [loading, setLoading] = useState(false);
  const voiceCapture = useVoiceTaskDrafts({ teamId: selectedTeam, onError: (msg) => toast.error(msg) });
  const aiModels = useAiModels();
  
  // Query state
  const [query, setQuery] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  
  // Task generation state
  const [taskDescription, setTaskDescription] = useState('');
  const [generatedTask, setGeneratedTask] = useState(null);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [workloadAnalysis, setWorkloadAnalysis] = useState(null);
  const [burnoutData, setBurnoutData] = useState(null);
  
  // Daily digest state
  const [dailyDigest, setDailyDigest] = useState(null);

  useEffect(() => {
    fetchTeams();
    fetchDailyDigest();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchAnalytics();
      fetchWorkloadAnalysis();
      fetchBurnoutDetection();
    }
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      if (response.data.length > 0) {
        setSelectedTeam(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch teams');
    }
  };

  const fetchDailyDigest = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/users/daily-digest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyDigest(response.data);
    } catch (error) {
      console.error('Failed to fetch daily digest');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams/${selectedTeam}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics');
    }
  };

  const fetchWorkloadAnalysis = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams/${selectedTeam}/workload-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkloadAnalysis(response.data);
    } catch (error) {
      console.error('Failed to fetch workload analysis');
    }
  };

  const fetchBurnoutDetection = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams/${selectedTeam}/burnout-detection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBurnoutData(response.data);
    } catch (error) {
      console.error('Failed to fetch burnout data');
    }
  };

  const handleQuery = async () => {
    if (!query.trim()) {
      toast.error('Please enter a query');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/query`,
        { query, team_id: selectedTeam },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQueryResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Query failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTask = async () => {
    if (!taskDescription.trim()) {
      toast.error('Please describe the task');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/tasks/generate?description=${encodeURIComponent(taskDescription)}&team_id=${selectedTeam}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedTask(response.data);
      toast.success('Task generated! Review and create it.');
    } catch (error) {
      toast.error('Task generation failed');
    } finally {
      setLoading(false);
    }
  };

  const createGeneratedTask = async () => {
    if (!generatedTask) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API}/tasks`,
        {
          title: generatedTask.title,
          description: generatedTask.description,
          team_id: selectedTeam,
          assigned_to: generatedTask.suggested_assignee
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task created successfully!');
      setGeneratedTask(null);
      setTaskDescription('');
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Sparkles size={32} style={{ color: '#10b981' }} />
            AI Assistant
          </h1>
          <p style={{ color: '#6b7280' }}>Intelligent insights and automation for your tasks</p>
        </div>
        <div className="ai-model-row" data-testid="ai-model-row">
          <span className="ai-model-label">Powered by ChatGPT ·</span>
          <ModelPicker models={aiModels.models} value={aiModels.selected} onChange={aiModels.select} disabled={aiModels.loading} testId="ai-model-picker" label="AI model for all features" />
        </div>
      </div>

      {/* Daily Digest */}
      {dailyDigest && (
        <Card style={{ padding: '1.5rem', marginBottom: '2rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }} data-testid="daily-digest-card">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              {dailyDigest.digest.greeting}
            </h2>
            <ListenButton
              id="daily-digest"
              className="on-dark"
              testId="listen-digest"
              text={[
                dailyDigest.digest.greeting,
                dailyDigest.digest.summary,
                dailyDigest.digest.priorities.length ? `Today's priorities: ${dailyDigest.digest.priorities.join('. ')}.` : '',
                dailyDigest.digest.quick_wins.length ? `Quick wins: ${dailyDigest.digest.quick_wins.join('. ')}.` : '',
              ].filter(Boolean).join(' ')}
            />
          </div>
          <p style={{ marginBottom: '1rem', opacity: 0.9 }}>{dailyDigest.digest.summary}</p>
          
          {dailyDigest.digest.priorities.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>🎯 Today's Priorities:</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {dailyDigest.digest.priorities.map((priority, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>• {priority}</li>
                ))}
              </ul>
            </div>
          )}

          {dailyDigest.digest.quick_wins.length > 0 && (
            <div>
              <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>⚡ Quick Wins:</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {dailyDigest.digest.quick_wins.map((win, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>• {win}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* Team Selector */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem' }}>Select Team</label>
        <Select value={selectedTeam} onValueChange={setSelectedTeam}>
          <SelectTrigger style={{ maxWidth: '300px' }}>
            <SelectValue placeholder="Select team" />
          </SelectTrigger>
          <SelectContent>
            {teams.map(team => (
              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #e5e7eb' }}>
        {[
          { id: 'query', label: 'Ask AI', icon: MessageSquare },
          { id: 'generate', label: 'Generate Task', icon: Sparkles },
          { id: 'analytics', label: 'Analytics', icon: TrendingUp },
          { id: 'workload', label: 'Workload', icon: Users },
          { id: 'burnout', label: 'Wellness', icon: AlertTriangle }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === tab.id ? '3px solid #10b981' : '3px solid transparent',
              color: activeTab === tab.id ? '#10b981' : '#6b7280',
              fontWeight: activeTab === tab.id ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Query Tab */}
      {activeTab === 'query' && (
        <Card style={{ padding: '2rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>Ask AI About Your Tasks</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Ask questions like "What tasks are overdue?" or "Who is working on the frontend?"
          </p>
          
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask anything about your tasks..."
              onKeyPress={(e) => e.key === 'Enter' && handleQuery()}
              style={{ flex: 1 }}
              data-testid="ai-query-input"
            />
            <Button onClick={handleQuery} disabled={loading} data-testid="ai-query-submit">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              <span style={{ marginLeft: '0.5rem' }}>Ask</span>
            </Button>
          </div>

          {queryResult && (
            <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px' }} data-testid="query-result">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
                <h3 style={{ fontWeight: 600 }}>Answer:</h3>
                <ListenButton id="ai-answer" text={queryResult.answer} testId="listen-answer" />
              </div>
              <p style={{ marginBottom: '1.5rem' }} data-testid="query-answer">{queryResult.answer}</p>
              
              {queryResult.relevant_tasks && queryResult.relevant_tasks.length > 0 && (
                <div>
                  <h4 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Relevant Tasks:</h4>
                  {queryResult.relevant_tasks.map(task => (
                    <div key={task.id} style={{ background: 'white', padding: '1rem', borderRadius: '6px', marginBottom: '0.5rem' }}>
                      <div style={{ fontWeight: 600 }}>{task.title}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Status: {task.status}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Generate Task Tab */}
      {activeTab === 'generate' && (
        <Card style={{ padding: '2rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>Generate Task from Description</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Describe what needs to be done in natural language, and AI will create a structured task — or tap the mic and just say it (up to 2 minutes, one or several tasks).
          </p>
          
          <div className="describe-row">
            <Textarea
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="E.g., 'We need to redesign the login page with better UX and add social login options'"
              rows={4}
              data-testid="task-description-input"
            />
            <VoiceCaptureButton capture={voiceCapture} disabled={!selectedTeam} />
          </div>
          
          <Button onClick={handleGenerateTask} disabled={loading} data-testid="generate-task-button">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
            <span style={{ marginLeft: '0.5rem' }}>Generate Task</span>
          </Button>

          <TaskDraftList capture={voiceCapture} teams={teams} user={user} />

          {generatedTask && (
            <div style={{ marginTop: '2rem', background: '#f0fdf4', padding: '1.5rem', borderRadius: '8px', border: '2px solid #10b981' }}>
              <h3 style={{ fontWeight: 600, marginBottom: '1rem', color: '#10b981' }}>✨ Generated Task</h3>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Title:</label>
                <div>{generatedTask.title}</div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Description:</label>
                <div>{generatedTask.description}</div>
              </div>
              
              <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Priority:</label>
                  <div style={{ textTransform: 'capitalize' }}>{generatedTask.priority}</div>
                </div>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Estimated Hours:</label>
                  <div>{generatedTask.estimated_hours}</div>
                </div>
              </div>

              {generatedTask.subtasks && generatedTask.subtasks.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Subtasks:</label>
                  <ul style={{ marginLeft: '1.5rem' }}>
                    {generatedTask.subtasks.map((subtask, idx) => (
                      <li key={idx}>{subtask}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <Button onClick={createGeneratedTask} style={{ background: '#10b981' }}>
                Create This Task
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          <Card style={{ padding: '1.5rem' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>Team Metrics</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                  {analytics.metrics.completion_rate}%
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Completion Rate</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.metrics.velocity}</div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Tasks/Week</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.metrics.total_tasks}</div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Total Tasks</div>
              </div>
            </div>
          </Card>

          <Card style={{ padding: '1.5rem' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>AI Insights</h3>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Health Score:</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: analytics.ai_insights.health_score > 70 ? '#10b981' : '#f59e0b' }}>
                {analytics.ai_insights.health_score}/100
              </div>
              <div style={{ color: '#6b7280', fontSize: '0.875rem', textTransform: 'capitalize' }}>
                Trend: {analytics.ai_insights.trend}
              </div>
            </div>
          </Card>

          <Card style={{ padding: '1.5rem', gridColumn: 'span 2' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>Key Insights</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {analytics.ai_insights.insights.map((insight, idx) => (
                <li key={idx} style={{ marginBottom: '0.5rem', paddingLeft: '1.5rem', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0 }}>💡</span>
                  {insight}
                </li>
              ))}
            </ul>
          </Card>

          {analytics.ai_insights.recommendations.length > 0 && (
            <Card style={{ padding: '1.5rem', gridColumn: 'span 2', background: '#fef3c7' }}>
              <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>Recommendations</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {analytics.ai_insights.recommendations.map((rec, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem', paddingLeft: '1.5rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0 }}>⭐</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {/* Workload Tab */}
      {activeTab === 'workload' && workloadAnalysis && (
        <div>
          <Card style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>Team Workload Distribution</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {workloadAnalysis.workload_data.map(member => (
                <div key={member.member_id} style={{ background: '#f9fafb', padding: '1rem', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{member.member_name}</div>
                  <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem' }}>
                    <div>Todo: <span style={{ fontWeight: 600 }}>{member.todo}</span></div>
                    <div>In Progress: <span style={{ fontWeight: 600 }}>{member.in_progress}</span></div>
                    <div>Done: <span style={{ fontWeight: 600, color: '#10b981' }}>{member.done}</span></div>
                    <div>Total Active: <span style={{ fontWeight: 600 }}>{member.total_active}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {workloadAnalysis.ai_analysis.overloaded_members.length > 0 && (
            <Card style={{ padding: '1.5rem', background: '#fef2f2', border: '2px solid #ef4444' }}>
              <h3 style={{ fontWeight: 600, marginBottom: '1rem', color: '#ef4444' }}>⚠️ Overloaded Members</h3>
              <ul style={{ marginLeft: '1.5rem' }}>
                {workloadAnalysis.ai_analysis.overloaded_members.map((member, idx) => (
                  <li key={idx}>{member}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {/* Burnout Tab */}
      {activeTab === 'burnout' && burnoutData && (
        <div>
          <Card style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>Team Wellness Score</h3>
            <div style={{ fontSize: '3rem', fontWeight: 'bold', color: burnoutData.ai_analysis.team_wellness_score > 70 ? '#10b981' : '#f59e0b' }}>
              {burnoutData.ai_analysis.team_wellness_score}/100
            </div>
          </Card>

          <Card style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>Individual Risk Assessment</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {burnoutData.risk_data.map(member => (
                <div 
                  key={member.member_id} 
                  style={{ 
                    background: member.risk_level === 'high' ? '#fef2f2' : member.risk_level === 'medium' ? '#fef3c7' : '#f0fdf4',
                    padding: '1rem', 
                    borderRadius: '6px',
                    border: `2px solid ${member.risk_level === 'high' ? '#ef4444' : member.risk_level === 'medium' ? '#f59e0b' : '#10b981'}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{member.member_name}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {member.active_tasks} active tasks
                      </div>
                    </div>
                    <div style={{ 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '9999px',
                      background: member.risk_level === 'high' ? '#ef4444' : member.risk_level === 'medium' ? '#f59e0b' : '#10b981',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }}>
                      {member.risk_level} Risk
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {burnoutData.ai_analysis.recommendations.length > 0 && (
            <Card style={{ padding: '1.5rem', background: '#eff6ff' }}>
              <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>💙 Wellness Recommendations</h3>
              {burnoutData.ai_analysis.recommendations.map((rec, idx) => (
                <div key={idx} style={{ marginBottom: '1rem', padding: '1rem', background: 'white', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 600 }}>{rec.member}</div>
                  <div style={{ color: '#6b7280' }}>{rec.action}</div>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
